// src/utils/fiscalDefaults.js

/**
 * Returns default service tax retention rates based on tax regime (CRT).
 * CRT 1/2/4 (Simples/MEI) = all zeros
 * CRT 3 (Regime Normal) = standard rates
 *
 * @param {string|number} taxRegime - string like 'simples_nacional' or CRT number (1-4)
 * @returns {object} field values for form.setFieldsValue
 */
export function getServiceTaxDefaults(taxRegime) {
  const isRegimeNormal = checkIsRegimeNormal(taxRegime);

  return {
    pisTaxRate: isRegimeNormal ? 0.65 : 0,
    cofinsTaxRate: isRegimeNormal ? 3 : 0,
    csllTaxRate: isRegimeNormal ? 1 : 0,
    irrfTaxRate: isRegimeNormal ? 1.5 : 0,
    inssTaxRate: isRegimeNormal ? 11 : 0,
  };
}

/**
 * Returns default product tax rates based on tax regime (CRT).
 * Prioritizes company-level defaults from fiscalData when available.
 *
 * @param {string|number} taxRegime - string like 'simples_nacional' or CRT number (1-4)
 * @param {object} [companyFiscalData] - storedSubscription.fiscalData
 * @returns {object} nested field values for form.setFieldsValue
 */
export function getProductTaxDefaults(taxRegime, companyFiscalData) {
  const isRegimeNormal = checkIsRegimeNormal(taxRegime);

  return {
    fiscalData: {
      pis: {
        rate: companyFiscalData?.defaultPisRate ?? (isRegimeNormal ? 0.65 : 0),
      },
      cofins: {
        rate: companyFiscalData?.defaultCofinsRate ?? (isRegimeNormal ? 3 : 0),
      },
      ipi: {
        rate: companyFiscalData?.defaultIpiRate ?? 0,
      },
      icms: {
        rate: companyFiscalData?.defaultIcmsRate ?? 0,
      },
    },
  };
}

/**
 * Converts taxRegime (string or number) to CRT code (1-4).
 * @param {string|number} taxRegime
 * @returns {number} CRT code
 */
export function getCrtCode(taxRegime) {
  if (typeof taxRegime === 'number') return taxRegime;

  if (typeof taxRegime === 'string') {
    const regime = taxRegime.toLowerCase();
    if (regime.includes('mei')) return 4;
    if (regime.includes('simples') && regime.includes('excess')) return 2;
    if (regime.includes('simples')) return 1;
    if (regime.includes('normal') || regime.includes('lucro')) return 3;
  }

  return 1; // default: Simples Nacional
}

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

function checkIsRegimeNormal(taxRegime) {
  const crt = getCrtCode(taxRegime);
  return crt === 3;
}
